import { SxProps, Theme } from "@mui/material";
import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { colorPalette } from "../../colors/colors";

const ASSESSMENT_ACCENT = colorPalette.blue400;
const ASSESSMENT_ACCENT_RGB = "6, 182, 212";

export type InteractiveColorPalette = {
  purple: string;
  purpleDark: string;
  purpleLight: string;
  purpleChip: string;
  purpleBorder: string;
  green: string;
  greenLight: string;
  greenBorder: string;
  red: string;
  redLight: string;
  greyText: string;
  cardBg: string;
  accentRgb: string;
};

export const LESSON_INTERACTIVE_COLORS: InteractiveColorPalette = {
  purple: "#7C3AED",
  purpleDark: "#5B21B6",
  purpleLight: "#EDE9FE",
  purpleChip: "#DDD6FE",
  purpleBorder: "#A78BFA",
  green: "#16A34A",
  greenLight: "#DCFCE7",
  greenBorder: "#86EFAC",
  red: "#DC2626",
  redLight: "#FEE2E2",
  greyText: "#6B7280",
  cardBg: "#FFFFFF",
  accentRgb: "124, 58, 237",
};

export const getInteractiveColorPalette = (
  variant: "lesson" | "assessment",
  theme: Theme,
): InteractiveColorPalette => {
  if (variant === "lesson") {
    return {
      ...LESSON_INTERACTIVE_COLORS,
      cardBg:
        theme.palette.mode === "dark"
          ? theme.palette.background.paper
          : LESSON_INTERACTIVE_COLORS.cardBg,
    };
  }

  if (theme.palette.mode === "dark") {
    return {
      purple: ASSESSMENT_ACCENT,
      purpleDark: ASSESSMENT_ACCENT,
      purpleLight: colorPalette.blue40010,
      purpleChip: colorPalette.blue40010,
      purpleBorder: ASSESSMENT_ACCENT,
      green: LESSON_INTERACTIVE_COLORS.green,
      greenLight: LESSON_INTERACTIVE_COLORS.greenLight,
      greenBorder: LESSON_INTERACTIVE_COLORS.greenBorder,
      red: LESSON_INTERACTIVE_COLORS.red,
      redLight: LESSON_INTERACTIVE_COLORS.redLight,
      greyText: theme.palette.text.secondary,
      cardBg: theme.palette.background.paper,
      accentRgb: ASSESSMENT_ACCENT_RGB,
    };
  }

  return {
    purple: ASSESSMENT_ACCENT,
    purpleDark: ASSESSMENT_ACCENT,
    purpleLight: colorPalette.blue50,
    purpleChip: colorPalette.blue50,
    purpleBorder: ASSESSMENT_ACCENT,
    green: LESSON_INTERACTIVE_COLORS.green,
    greenLight: LESSON_INTERACTIVE_COLORS.greenLight,
    greenBorder: LESSON_INTERACTIVE_COLORS.greenBorder,
    red: LESSON_INTERACTIVE_COLORS.red,
    redLight: LESSON_INTERACTIVE_COLORS.redLight,
    greyText: LESSON_INTERACTIVE_COLORS.greyText,
    cardBg: "#FFFFFF",
    accentRgb: ASSESSMENT_ACCENT_RGB,
  };
};

export const useInteractivePalette = (isAssessmentMode = false) => {
  const theme = useTheme();

  return useMemo(
    () =>
      getInteractiveColorPalette(
        isAssessmentMode ? "assessment" : "lesson",
        theme,
      ),
    [isAssessmentMode, theme],
  );
};

export const createInteractiveStyles = (
  palette: InteractiveColorPalette,
  theme: Theme,
) => {
  const titleSx: SxProps<Theme> = {
    fontWeight: 700,
    fontSize: { xs: "1.125rem", sm: "1.25rem" },
    color: palette.purple,
    lineHeight: 1.3,
  };

  const instructionSx: SxProps<Theme> = {
    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
    color:
      theme.palette.mode === "dark"
        ? theme.palette.text.secondary
        : palette.greyText,
    lineHeight: 1.45,
  };

  const promptSx: SxProps<Theme> = {
    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
    lineHeight: 1.45,
    fontWeight: 600,
    color: theme.palette.text.primary,
  };

  const cardSx: SxProps<Theme> = {
    backgroundColor: palette.cardBg,
    borderRadius: "12px",
    border: `1px solid ${palette.purpleLight}`,
    boxShadow: `0 1px 3px rgba(${palette.accentRgb}, 0.08)`,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    p: { xs: 1, sm: 1.5, md: 2 },
  };

  const poolLabelSx: SxProps<Theme> = {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color:
      theme.palette.mode === "dark" ? palette.purple : palette.purpleDark,
    mb: 1,
  };

  const sentenceBoxSx: SxProps<Theme> = {
    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
    lineHeight: 1.7,
    color: theme.palette.text.primary,
  };

  const dropTargetActiveSx: SxProps<Theme> = {
    borderColor: `${palette.purple} !important`,
    backgroundColor: `${palette.purpleLight} !important`,
    boxShadow: `inset 0 0 0 2px rgba(${palette.accentRgb}, 0.2)`,
  };

  const touchDragGhostSx: SxProps<Theme> = {
    position: "absolute",
    zIndex: 10,
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "max-content",
    minWidth: "max-content",
    maxWidth: "none",
    flexShrink: 0,
    whiteSpace: "nowrap",
    px: 1.5,
    py: 0.75,
    borderRadius: "10px",
    backgroundColor: palette.purpleChip,
    border: `2px solid ${palette.purple}`,
    color: palette.purpleDark,
    fontSize: "0.875rem",
    fontWeight: 600,
    boxShadow: `0 8px 24px rgba(${palette.accentRgb}, 0.35)`,
    lineHeight: 1.35,
  };

  const slotActionButtonSx: SxProps<Theme> = {
    minWidth: "auto",
    px: 0.75,
    py: 0.25,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: palette.purple,
    "&:hover": {
      backgroundColor: palette.purpleLight,
    },
    "&.Mui-disabled": {
      color: "#D1D5DB",
    },
  };

  const getOptionChipSx = (
    isDragging?: boolean,
    highlight?: "correct" | "incorrect" | "neutral",
  ): SxProps<Theme> => {
    let bg = palette.purpleChip;
    let border = `1px solid ${palette.purpleBorder}`;
    let color = palette.purpleDark;

    if (highlight === "correct") {
      bg = palette.greenLight;
      border = `1px solid ${palette.greenBorder}`;
      color = palette.green;
    } else if (highlight === "incorrect") {
      bg = palette.redLight;
      border = `1px solid ${palette.red}`;
      color = palette.red;
    }

    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      px: 1.5,
      py: 0.75,
      borderRadius: "10px",
      backgroundColor: bg,
      border,
      color,
      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
      fontWeight: 600,
      cursor: "grab",
      userSelect: "none",
      WebkitUserSelect: "none",
      WebkitTouchCallout: "none",
      touchAction: "none",
      opacity: isDragging ? 0.35 : 1,
      transition: "box-shadow 0.15s, transform 0.15s",
      "&:hover": {
        boxShadow: `0 2px 6px rgba(${palette.accentRgb}, 0.2)`,
      },
      "&:active": {
        cursor: "grabbing",
      },
    };
  };

  const getBlankSlotSx = (
    filled: boolean,
    result?: "correct" | "incorrect",
    editable?: boolean,
  ): SxProps<Theme> => {
    let borderColor = palette.purpleBorder;
    let bg =
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#FAFAFF";

    if (result === "correct") {
      borderColor = palette.green;
      bg = palette.greenLight;
    } else if (result === "incorrect") {
      borderColor = palette.red;
      bg = palette.redLight;
    }

    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: { xs: 72, sm: 88 },
      minHeight: 32,
      px: 1,
      py: 0.5,
      mx: 0.25,
      my: 0.25,
      verticalAlign: "middle",
      borderRadius: "8px",
      border: `2px dashed ${borderColor}`,
      backgroundColor: filled ? bg : palette.purpleLight,
      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
      fontWeight: 600,
      color: palette.purpleDark,
      ...(editable && filled && !result
        ? { cursor: "pointer", "&:active": { opacity: 0.85 } }
        : {}),
    };
  };

  const orderedResponseSlotSx = (
    filled: boolean,
    result?: "correct" | "incorrect",
  ): SxProps<Theme> => {
    let borderColor = palette.purpleBorder;
    let bg =
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#FAFAFF";

    if (result === "correct") {
      borderColor = palette.green;
      bg = palette.greenLight;
    } else if (result === "incorrect") {
      borderColor = palette.red;
      bg = palette.redLight;
    }

    return {
      flex: 1,
      minWidth: 0,
      minHeight: 44,
      display: "flex",
      alignItems: "center",
      borderRadius: "10px",
      border: `1px solid ${borderColor}`,
      backgroundColor: filled ? bg : palette.purpleLight,
      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
      fontWeight: 500,
      color: filled ? palette.purpleDark : palette.greyText,
      px: { xs: 1, sm: 1.25 },
      py: 1,
      lineHeight: 1.4,
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    };
  };

  const orderingSlotSx = (
    filled: boolean,
    result?: "correct" | "incorrect",
    editable?: boolean,
  ): SxProps<Theme> => {
    let borderColor = palette.purpleBorder;
    let bg =
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#FAFAFF";

    if (result === "correct") {
      borderColor = palette.green;
      bg = palette.greenLight;
    } else if (result === "incorrect") {
      borderColor = palette.red;
      bg = palette.redLight;
    }

    return {
      flex: 1,
      minHeight: 44,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "10px",
      border: `2px dashed ${borderColor}`,
      backgroundColor: filled ? bg : palette.purpleLight,
      fontSize: "0.8125rem",
      fontWeight: 600,
      color: filled ? palette.purpleDark : palette.greyText,
      px: 1,
      ...(editable && filled && !result
        ? { cursor: "pointer", "&:active": { opacity: 0.85 } }
        : {}),
    };
  };

  const matchingColumnCardSx = (
    side: "left" | "right",
    highlight?: "correct" | "incorrect" | "selected",
  ): SxProps<Theme> => {
    const isLeft = side === "left";
    let bg = isLeft ? palette.purpleLight : palette.greenLight;
    let border = isLeft
      ? `1px solid ${palette.purpleBorder}`
      : `1px solid ${palette.greenBorder}`;

    if (highlight === "correct") {
      bg = palette.greenLight;
      border = `2px solid ${palette.green}`;
    } else if (highlight === "incorrect") {
      bg = palette.redLight;
      border = `2px solid ${palette.red}`;
    } else if (highlight === "selected") {
      border = `2px solid ${palette.purple}`;
    }

    return {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: { xs: 0.5, sm: 1 },
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      px: { xs: 0.75, sm: 1.25 },
      py: { xs: 0.75, sm: 1 },
      borderRadius: "10px",
      backgroundColor: bg,
      border,
      fontSize: { xs: "0.6875rem", sm: "0.75rem", md: "0.8125rem" },
      fontWeight: 600,
      color: isLeft ? palette.purpleDark : palette.green,
      cursor: "pointer",
      minHeight: 44,
    };
  };

  const matchingDotSx = (side: "left" | "right"): SxProps<Theme> => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: side === "left" ? palette.purple : palette.green,
  });

  const submitButtonSx = (disabled: boolean): SxProps<Theme> => ({
    width: "100%",
    mt: 0.5,
    py: 1.25,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    backgroundColor: disabled ? "#E5E7EB" : palette.purple,
    color: disabled ? "#9CA3AF" : "#FFFFFF",
    boxShadow: disabled ? "none" : `0 4px 12px rgba(${palette.accentRgb}, 0.35)`,
    "&:hover": {
      backgroundColor: disabled ? "#E5E7EB" : palette.purpleDark,
    },
  });

  return {
    palette,
    titleSx,
    instructionSx,
    promptSx,
    cardSx,
    poolLabelSx,
    sentenceBoxSx,
    dropTargetActiveSx,
    touchDragGhostSx,
    slotActionButtonSx,
    getOptionChipSx,
    getBlankSlotSx,
    orderedResponseSlotSx,
    orderingSlotSx,
    matchingColumnCardSx,
    matchingDotSx,
    submitButtonSx,
  };
};

export type InteractiveStyleKit = ReturnType<typeof createInteractiveStyles>;

export const useInteractiveStyles = (isAssessmentMode = false) => {
  const theme = useTheme();
  const palette = useInteractivePalette(isAssessmentMode);

  return useMemo(
    () => createInteractiveStyles(palette, theme),
    [palette, theme],
  );
};
